import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Loader2, RefreshCw, Send, ShieldAlert } from "lucide-react";
import { Btn, FieldRow, Section, inputCls } from "@/components/account/Shell";
import { Cell, Dash, IconBtn, Sheet, Tag, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";

const HOSTS = {
    networksolutions: {
        name: "Network Solutions",
        steps: [
            "Sign in at networksolutions.com → My Account → Manage Domains",
            "Click 13thav.com → Change Where Domain Points → Advanced DNS",
            "Under TXT Records click Edit TXT Records, add the two TXT rows below (Host = the Name column, no “.13thav.com” suffix)",
            "Under Mail Servers (MX) click Edit MX Records and add the MX row with priority 10",
            "Save, wait 5–30 minutes, then hit Check verification here",
        ],
    },
};
const DNS_COLS = [
    { key: "type", label: "Type", w: "70px" },
    { key: "record", label: "Record", sub: "what it's for", w: "110px" },
    { key: "name", label: "Name / host", sub: "click to copy", w: "minmax(200px,1fr)" },
    { key: "value", label: "Value", sub: "click to copy", w: "minmax(300px,2fr)" },
    { key: "priority", label: "Priority", w: "80px" },
    { key: "ttl", label: "TTL", w: "70px" },
    { key: "status", label: "Status", w: "120px" },
];

export default function EmailDelivery() {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [testTo, setTestTo] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/email/domain").then(({ data }) => setInfo(data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const verify = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/email/domain/verify");
            setInfo(data);
            toast[data.verified ? "success" : "message"](data.verified
                ? "Domain verified — proofs now send from your own address"
                : `Still ${data.status.replace(/_/g, " ")} — DNS can take up to 30 minutes`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const addDomain = async () => {
        setBusy(true);
        try { const { data } = await api.post("/admin/email/domain"); setInfo(data); toast.success("Domain added to Resend"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const sendTest = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/email/test", { to: testTo });
            data.sent ? toast.success(`Test sent from ${data.from}${data.note ? ` — ${data.note}` : ""}`) : toast.error(data.reason);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

    if (loading) return <div className="flex justify-center py-16 text-[#4a4a4a]"><Loader2 className="animate-spin" /></div>;
    if (!info) return <p className="text-sm text-[#4a4a4a]">Could not load email settings.</p>;
    const host = HOSTS.networksolutions;
    const copyBtn = (v, testid) => (
        <button onClick={() => copy(v)} data-testid={testid} title="Copy" className="flex min-w-0 flex-1 items-center justify-between gap-1.5 border border-[#d0d0d0] bg-white px-1.5 py-0.5 text-left font-mono text-[11px] text-[#1a1a1a] hover:border-[#1a1a1a]">
            <span className="truncate">{v}</span><Copy size={10} className="shrink-0 text-[#4a4a4a]" />
        </button>
    );

    return (
        <div className="space-y-3" data-testid="admin-email">
            <Section title="Proof emails send from" pad={false} testid="email-sender"
                right={
                    <Tag status={info.verified ? "active" : "pending"} testid="email-domain-status">
                        {info.verified ? <CheckCircle2 size={10} /> : <ShieldAlert size={10} />} {String(info.status).replace(/_/g, " ")}
                    </Tag>
                }>
                <FieldRow label="Sender" hint="the address on every proof and receipt">
                    <p className="text-sm font-semibold text-[#1a1a1a]">{info.sender}</p>
                </FieldRow>
                {!info.verified && (
                    <FieldRow label="Until it's verified" hint="what happens today">
                        <p className="text-[13px] text-[#1a1a1a]">
                            Proofs go out from Resend's test sender (<span className="font-mono">{info.fallback_sender}</span>), which only reaches your own
                            Resend inbox. Add the DNS records below to switch to {info.sender}.
                        </p>
                    </FieldRow>
                )}
                <FieldRow label="Actions">
                    <div className="flex flex-wrap gap-1.5">
                        {info.status === "missing"
                            ? <Btn tone="solid" onClick={addDomain} disabled={busy} testid="email-add-domain">Add {info.domain} to Resend</Btn>
                            : <Btn tone="solid" onClick={verify} disabled={busy} testid="email-verify">
                                {busy ? <Loader2 className="animate-spin" size={11} /> : <RefreshCw size={11} />} Check verification
                            </Btn>}
                        <Btn onClick={load} disabled={busy} testid="email-refresh"><RefreshCw size={11} /> Refresh</Btn>
                    </div>
                </FieldRow>
            </Section>

            {info.records?.length > 0 && (
                <Section title="DNS records to add" pad={false} testid="email-dns" note="add these at your DNS host, then hit Check verification">
                    <Sheet cols={DNS_COLS} rows={info.records} rowKey={(r, i) => `${r.type}-${r.name}`} testid="dns-sheet" minWidth={960}
                        rowTestid={() => "dns-record"}
                        cells={(r) => ({
                            type: <Cell><Tag status="booked">{r.type}</Tag></Cell>,
                            record: <Cell><Txt sub>{r.record}</Txt></Cell>,
                            name: <Cell>{copyBtn(r.name)}</Cell>,
                            value: <Cell>{copyBtn(r.value)}</Cell>,
                            priority: <Cell>{r.priority ? <Txt mono right>{r.priority}</Txt> : <Dash why="No priority on this record" />}</Cell>,
                            ttl: <Cell><Txt mono right>{r.ttl || "auto"}</Txt></Cell>,
                            status: <Cell>{r.status ? <Tag status={r.status === "verified" ? "active" : "pending"}>{String(r.status).replace(/_/g, " ")}</Tag> : <Dash why="Not checked yet" />}</Cell>,
                        })} />
                    <div className="border-t border-[#d0d0d0] px-2.5 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{host.name} — where to click</p>
                        <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-[13px] text-[#1a1a1a]">
                            {host.steps.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                    </div>
                </Section>
            )}

            <Section title="Send a test email" pad={false} testid="email-test">
                <FieldRow label="Send to" hint="any inbox you can open">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourshop.com"
                            className={`${inputCls} max-w-[320px]`} data-testid="email-test-input" />
                        <Btn tone="solid" onClick={sendTest} disabled={busy || !testTo.includes("@")} testid="email-test-send">
                            {busy ? <Loader2 className="animate-spin" size={11} /> : <Send size={11} />} Send test
                        </Btn>
                        <IconBtn onClick={load} title="Refresh" testid="email-test-refresh"><RefreshCw size={12} /></IconBtn>
                    </div>
                </FieldRow>
            </Section>
        </div>
    );
}
